import Layout from "@/components/layouts/Layout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FileText, Users, Shield, AlertTriangle, Scale, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const TermsOfService = () => {
  const { t, language } = useLanguage();

  // Helper function to safely get array translations with proper typing
  const getArrayTranslation = (key: string): string[] => {
    const translation = t(key);
    return Array.isArray(translation) ? translation : [];
  };

  // Helper component for list items with consistent styling
  const ListItem = ({ text }: { text: string }) => (
    <li className="flex items-start gap-3">
      <div className="w-2 h-2 bg-nexus-gray rounded-full mt-2 flex-shrink-0" />
      <span className="text-gray-600 dark:text-gray-400">{text}</span>
    </li>
  );

  // Helper component for bullet list items
  const BulletListItem = ({ text }: { text: string }) => (
    <li className="text-gray-600 dark:text-gray-400">• {text}</li>
  );

  return (
    <Layout>
      <div className="bg-gradient-to-b from-nexus-gray/5 to-white py-16">
        <div className="container">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-nexus-gray/10 rounded-lg flex items-center justify-center mx-auto mb-6">
              <Scale className="h-8 w-8 text-nexus-gray" />
            </div>
            <h1 className="text-4xl font-bold text-nexus-gray dark:text-gray-200 mb-4">
              {t("termsOfService.title")}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-200 max-w-2xl mx-auto">
              {t("termsOfService.description")}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              {t("termsOfService.lastUpdated")}: {new Date().toLocaleDateString(language)}
            </p>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Acceptance of Terms */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <FileText className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.acceptance.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.acceptance.content")}
                </p>
              </CardContent>
            </Card>

            {/* Services Description */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Users className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.services.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.services.content")}
                </p>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">
                    {t("termsOfService.sections.services.categories.title")}
                  </h3>
                  <ul className="space-y-2 ml-4">
                    {getArrayTranslation("termsOfService.sections.services.categories.items").map(
                      (item, index) => (
                        <BulletListItem key={index} text={item} />
                      )
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* User Responsibilities */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Shield className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.responsibilities.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  {t("termsOfService.sections.responsibilities.intro")}
                </p>
                <ul className="space-y-3 ml-4">
                  {getArrayTranslation("termsOfService.sections.responsibilities.items").map(
                    (item, index) => (
                      <ListItem key={index} text={item} />
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Prohibited Uses */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  {t("termsOfService.sections.prohibited.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  {t("termsOfService.sections.prohibited.intro")}
                </p>
                <ul className="space-y-2 ml-4">
                  {getArrayTranslation("termsOfService.sections.prohibited.items").map(
                    (item, index) => (
                      <BulletListItem key={index} text={item} />
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <FileText className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.intellectualProperty.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.intellectualProperty.content")}
                </p>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Scale className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.liability.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.liability.content")}
                </p>
              </CardContent>
            </Card>

            {/* Service Availability */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Shield className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.availability.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.availability.content")}
                </p>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <AlertTriangle className="h-5 w-5 text-nexus-yellow" />
                  {t("termsOfService.sections.changes.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.changes.content")}
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Mail className="h-5 w-5 text-nexus-gray" />
                  {t("termsOfService.sections.contact.title")}
                </h2>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t("termsOfService.sections.contact.content")}
                </p>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    {t("termsOfService.sections.contact.details.email")}<br />
                    {t("termsOfService.sections.contact.details.address")}<br />
                    {t("termsOfService.sections.contact.details.businessAddress")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsOfService;
